/**
 * User Guide — `/user-guide`.
 *
 * The onboarding manual for Depletions, Regional Sales, and Regional
 * Sales Oversight. Renders from the product's own catalogs
 * (`data/depletionsSections.ts`, `data/regionalSalesGuide.ts`,
 * `data/guideGlossary.ts`) plus the narrative in `data/userGuide.ts`,
 * so the manual cannot describe sections the dashboard no longer has.
 *
 * Three modes, one component:
 *
 *   • Normal      — sidebar contents, live search, screenshot lightbox.
 *   • `?print=1`  — sidebar and chrome dropped, every image inlined,
 *                   page-break hints applied. This is what the capture
 *                   script renders to PDF, and what the browser's own
 *                   Print gets via the same query parameter.
 *
 * Screenshots live in `public/images/guide/` and are referenced by the
 * catalog `id` of the thing they illustrate. A missing image renders a
 * labelled placeholder rather than a broken image, so the guide always
 * ships even when a shot has not been taken yet.
 */

import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Chip, Collapse, Container, Dialog, Typography } from '@mui/material';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PREMISES_STYLE } from '../components/premisesStyle';
import { SiteLayout } from '../components/SiteLayout';
import { VISIT_OUTCOME_TONES } from '../api/field';
import { CONTROL_STATES } from '../lib/controlStates';
import { BUCKET_META, BUCKET_ORDER } from '../lib/followUpBuckets';
import { OUTCOME_GUIDE, OUTCOME_ORDER } from '../lib/visitOutcomes';
import {
  CATEGORY_ACCENT,
  depletionsSectionsByCategory,
  type SectionGuide,
} from '../data/depletionsSections';
import { GLOSSARY, GLOSSARY_AREAS } from '../data/guideGlossary';
import { chapterReadingMinutes, totalReadingMinutes } from '../data/guideReadingTime';
import { shotAspectRatio } from '../data/guideShots';
import { FEEDBACK_CATEGORIES } from '../lib/feedbackCategories';
import {
  OVERSIGHT_TOPICS,
  REGIONAL_SALES_ACTIONS,
  REGIONAL_SALES_TABS,
  type GuideTopic,
} from '../data/regionalSalesGuide';
import {
  ACCESS_NOTES,
  AUTH_CAUTION,
  AUTH_TOPICS,
  CHAPTER_INTROS,
  ADMIN_CAPABILITIES,
  AREA_OVERVIEW,
  FAQ,
  FEEDBACK_INTRO,
  FEEDBACK_NOTES,
  GETTING_STARTED,
  GUIDE_REVISIONS,
  GUIDE_VERSION,
  INTRODUCTION,
  ROLES,
  SESSION_ENDINGS,
  SESSION_RATIONALE,
  type GuideParagraph,
} from '../data/userGuide';
import { colors, docLeading, docType } from '../theme';

const GOLD = colors.goldDark;
const INK = colors.textPrimary;
const FONT_SERIF = '"Playfair Display", Georgia, serif';

interface Chapter {
  id: string;
  /** Position in the book. Assigned from CHAPTER_ORDER, never authored. */
  number: number;
  title: string;
  /** One-line framing shown under the chapter heading. */
  blurb: string;
  /** Accent colour, so the contents list reads as a designed set. */
  tone: string;
  /** Everything in the chapter requires the Administrator role. */
  adminOnly?: boolean;
}

/**
 * Chapters keyed by name rather than held in a bare array, so every
 * reference below is a checked property access instead of an index
 * lookup that TypeScript has to widen to `Chapter | undefined`.
 */
const CHAPTER_DEFS = {
  introduction: {
    id: 'introduction',
    title: 'Before you start',
    blurb: 'The few ideas everything else rests on.',
    tone: GOLD,
  },
  gettingStarted: {
    id: 'getting-started',
    title: 'Getting started',
    blurb: 'Getting an account and finding your way in.',
    tone: GOLD,
  },
  depletions: {
    id: 'depletions',
    title: 'Depletions',
    blurb: 'The analytical dashboard, section by section.',
    tone: '#a86b1e',
  },
  regionalSales: {
    id: 'regional-sales',
    title: 'Regional Sales',
    blurb: 'The representative’s daily workspace, tab by tab.',
    tone: '#8e4dab',
  },
  oversight: {
    id: 'oversight',
    title: 'Regional Sales Oversight',
    blurb: 'The management view of the field team.',
    tone: '#bb8c3f',
    adminOnly: true,
  },
  access: {
    id: 'access',
    title: 'Authentication & Authorization',
    blurb: 'Proving who you are, and what that lets you reach.',
    tone: '#3f7d8c',
  },
  glossary: {
    id: 'glossary',
    title: 'Glossary',
    blurb: 'Every term used in the product.',
    tone: '#22865a',
  },
  faq: {
    id: 'faq',
    title: 'Common questions',
    blurb: 'The things people ask in week one.',
    tone: '#326eb8',
  },
  feedback: {
    id: 'feedback',
    title: 'Tell us what’s missing',
    blurb: 'How to reach the team that builds this.',
    tone: '#9b2a48',
  },
} satisfies Record<string, Omit<Chapter, 'number'>>;

/**
 * Reading order. The single place chapter position is decided.
 *
 * Numbers used to be authored on each chapter, which meant inserting one
 * required renumbering every chapter after it by hand — four silent
 * chances to end up with two chapter 6s. Deriving them from this array
 * makes insertion a one-line change that cannot drift.
 */
const CHAPTER_ORDER = [
  'introduction',
  'gettingStarted',
  'depletions',
  'regionalSales',
  'oversight',
  'glossary',
  'faq',
  // Last of the substantive chapters. It is the one technical subject in
  // a book about running a sales territory, so it sits after everything
  // a rep opens the guide to find rather than interrupting it — with the
  // feedback note still closing the book.
  'access',
  'feedback',
] as const satisfies readonly (keyof typeof CHAPTER_DEFS)[];

const CHAPTER = Object.fromEntries(
  CHAPTER_ORDER.map((key, i) => [key, { ...CHAPTER_DEFS[key], number: i + 1 }]),
) as Record<(typeof CHAPTER_ORDER)[number], Chapter>;

// =====================================================================
// Table of contents + scroll spy
// =====================================================================

/** Height of the app's sticky header, plus breathing room. A heading is
 *  considered "current" once it crosses this line. */
const ACTIVATION_OFFSET = 110;

/** A leaf entry — one documented feature inside a chapter. */
export interface TocTopic {
  /** DOM id of the heading, as set by `TopicBlock`. */
  id: string;
  label: string;
}

/** A chapter and the topics nested under it. */
export interface TocChapter {
  /** DOM id of the heading, as set by `ChapterHeading`. */
  id: string;
  number: number;
  title: string;
  tone: string;
  /** Everything in the chapter requires the Administrator role. */
  adminOnly?: boolean;
  topics: TocTopic[];
}

/**
 * The contents tree, derived from the same catalogs that render the
 * body — so a new dashboard section shows up in the sidebar without
 * anyone maintaining a second list.
 */
function buildToc(): TocChapter[] {
  const chapter = (c: Chapter, topics: TocTopic[] = []): TocChapter => ({
    id: c.id,
    number: c.number,
    title: c.title,
    tone: c.tone,
    adminOnly: c.adminOnly,
    topics,
  });
  const topic = (t: { id: string; title: string }): TocTopic => ({
    id: `topic-${t.id}`,
    label: t.title,
  });

  // Topics per chapter; order comes from CHAPTER_ORDER below, so the
  // rail cannot end up listing chapters in a different sequence from the
  // numbers it prints beside them.
  const topics: Partial<Record<(typeof CHAPTER_ORDER)[number], TocTopic[]>> = {
    depletions: depletionsSectionsByCategory().flatMap((g) => g.sections.map(topic)),
    regionalSales: [...REGIONAL_SALES_TABS, ...REGIONAL_SALES_ACTIONS].map(topic),
    oversight: OVERSIGHT_TOPICS.map(topic),
  };

  return CHAPTER_ORDER.map((key) => chapter(CHAPTER[key], topics[key] ?? []));
}

/** Every heading id in document order — what the scroll spy measures. */
function flattenToc(chapters: TocChapter[]): string[] {
  return chapters.flatMap((c) => [c.id, ...c.topics.map((t) => t.id)]);
}

/** The chapter that owns a heading id, whether it is the chapter's own
 *  heading or one of its topics. */
function chapterOf(chapters: TocChapter[], id: string | null): TocChapter | null {
  if (!id) return null;
  return chapters.find((c) => c.id === id || c.topics.some((t) => t.id === id)) ?? null;
}

/**
 * Which heading is currently being read.
 *
 * Measures against the viewport rather than caching offsets: screenshots
 * load lazily and the window resizes, either of which invalidates cached
 * positions. One `getBoundingClientRect` per entry per animation frame is
 * cheap at this list size, and it stays correct without invalidation
 * bookkeeping.
 *
 * The last entry wins once the page is scrolled to the bottom — the final
 * headings are too close to the end of the document to ever cross the
 * activation line on their own.
 */
function useScrollSpy(
  ids: string[],
  enabled: boolean,
): { activeId: string | null; jumpTo: (id: string) => void } {
  const [activeId, setActiveId] = useState<string | null>(null);
  // Set while a programmatic scroll is in flight. A smooth scroll fires
  // `scroll` on every frame, so without this the spy walks the reader
  // through every heading between here and the destination — and the
  // contents rail expands and collapses each chapter on the way past.
  const lockRef = useRef(false);
  const releaseRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      setActiveId(null);
      return;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      if (lockRef.current) return;
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 8;
      if (atBottom) {
        const last = ids[ids.length - 1];
        if (last) setActiveId(last);
        return;
      }
      let current: string | null = ids[0] ?? null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= ACTIVATION_OFFSET) current = id;
        else break;
      }
      setActiveId(current);
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [ids, enabled]);

  /**
   * Move straight to an entry: mark it current, hold the spy until the
   * scroll settles, then hand control back.
   *
   * The release is position-based rather than timed. A smooth scroll
   * across a 26,000px document takes well over a second, and any fixed
   * timeout either releases early — the spy then catches an intermediate
   * heading and the rail flickers — or holds on long after arrival. This
   * simply waits until the target is where we asked it to go.
   *
   * It also releases the moment the reader takes over: if they scroll or
   * press a key mid-flight, their intent outranks the pending jump.
   */
  const jumpTo = (id: string) => {
    lockRef.current = true;
    setActiveId(id);
    window.clearTimeout(releaseRef.current);

    let release = () => {};
    const onUserInput = () => release();

    release = () => {
      lockRef.current = false;
      window.clearTimeout(releaseRef.current);
      window.removeEventListener('wheel', onUserInput);
      window.removeEventListener('touchstart', onUserInput);
      window.removeEventListener('keydown', onUserInput);
    };

    window.addEventListener('wheel', onUserInput, { passive: true, once: true });
    window.addEventListener('touchstart', onUserInput, { passive: true, once: true });
    window.addEventListener('keydown', onUserInput, { once: true });

    const startedAt = performance.now();
    const settle = () => {
      const el = document.getElementById(id);
      const arrived =
        !el || Math.abs(el.getBoundingClientRect().top - (ACTIVATION_OFFSET - 22)) < 6;
      // Hard cap so a scroll that never completes cannot strand the spy.
      if (arrived || performance.now() - startedAt > 4000) {
        release();
        return;
      }
      releaseRef.current = window.setTimeout(settle, 60);
    };
    releaseRef.current = window.setTimeout(settle, 60);
  };

  // Never leave the spy locked if the page unmounts mid-scroll.
  useEffect(() => () => window.clearTimeout(releaseRef.current), []);

  return { activeId, jumpTo };
}

/** Scroll a heading to just below the sticky header. */
function scrollToEntry(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - ACTIVATION_OFFSET + 22;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
}

// =====================================================================
// Screenshot
// =====================================================================

/** Resolve a catalog id to its screenshot path. */
function shotPath(id: string): string {
  return `/images/guide/${id}.png`;
}

interface ShotProps {
  id: string;
  caption: string;
  onOpen?: (src: string, caption: string) => void;
  print: boolean;
}

/**
 * A screenshot with a graceful missing state.
 *
 * We never want an un-captured shot to break the page or, worse, leave
 * a broken-image icon in a downloaded PDF — so a failed load swaps to a
 * labelled placeholder that names the shot the capture script owes us.
 */
function Shot({ id, caption, onOpen, print }: ShotProps) {
  const [failed, setFailed] = useState(false);
  const src = shotPath(id);

  if (failed) {
    return (
      <Box
        sx={{
          mt: 1.5,
          px: 2,
          border: `1px dashed ${colors.border}`,
          borderRadius: 2,
          bgcolor: colors.surfaceSubtle,
          // Same box the real shot would occupy, so a missing image
          // does not move the page around either.
          aspectRatio: `${shotAspectRatio(id)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: docType.small, color: colors.textMuted }}>
          Screenshot pending — <code>{`images/guide/${id}.png`}</code>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.75, breakInside: 'avoid' }}>
      <Box
        component="img"
        src={src}
        alt={caption}
        loading={print ? 'eager' : 'lazy'}
        onError={() => setFailed(true)}
        onClick={print ? undefined : () => onOpen?.(src, caption)}
        sx={{
          width: '100%',
          display: 'block',
          // Reserve the box before the image decodes. Without this a
          // lazy-loaded shot occupies no height, the page grows by
          // thousands of pixels as you scroll, and anchor links land
          // short of their target.
          aspectRatio: `${shotAspectRatio(id)}`,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          bgcolor: colors.surfaceSubtle,
          boxShadow: print ? 'none' : '0 1px 2px rgba(15,23,42,0.05)',
          cursor: print ? 'default' : 'zoom-in',
        }}
      />
      <Typography
        sx={{
          mt: 0.75,
          fontSize: docType.label,
          color: colors.textMuted,
          fontStyle: 'italic',
        }}
      >
        {caption}
      </Typography>
    </Box>
  );
}

/**
 * Marks something only administrators can reach.
 *
 * Gold on purpose: the platform already uses it for the Admin entry in
 * the user menu and the Oversight card on the home page, so the colour
 * already means "admin" to anyone who has used the product.
 */
function AdminChip({ label = 'Admin only' }: { label?: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        px: 0.85,
        py: 0.15,
        borderRadius: 1,
        bgcolor: `${GOLD}1a`,
        border: `1px solid ${GOLD}59`,
        color: GOLD,
        fontSize: docType.label,
        fontWeight: 700,
        letterSpacing: '0.04em',
        lineHeight: docLeading.snug,
        whiteSpace: 'nowrap',
      }}
    >
      <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 13 }} />
      {label}
    </Box>
  );
}

/**
 * "What an administrator sees or does here" — deliberately styled apart
 * from the neutral "Good to know" box, so a representative can tell at a
 * glance that a paragraph is about someone else's view of the product.
 */
function AdminNote({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        mt: 1.75,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: `${GOLD}0a`,
        border: `1px solid ${GOLD}33`,
        borderLeft: `3px solid ${GOLD}`,
        breakInside: 'avoid',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
        <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 14, color: GOLD }} />
        <Typography
          sx={{
            fontSize: docType.label,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: GOLD,
          }}
        >
          For administrators
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: docType.small,
          lineHeight: docLeading.normal,
          color: colors.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// =====================================================================
// Colour keys
// =====================================================================
//
// The product colour-codes buckets, outcomes, channels and momentum.
// Restating those as a comma-separated sentence teaches nothing — a
// reader has to recognise the colour on screen, so the guide shows the
// same swatch the product does, pulled from the same constants.

/** One row of a key: a coloured token, a term, and what it means. */
function KeyRow({
  token,
  term,
  children,
}: {
  token: ReactNode;
  term: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        // Stacks on narrow screens — a 130px token column plus prose is
        // unreadable on a phone.
        gridTemplateColumns: { xs: '1fr', sm: '132px 1fr' },
        columnGap: 2,
        rowGap: 0.5,
        alignItems: 'baseline',
        py: 1,
        borderTop: `1px solid ${colors.borderLight}`,
        breakInside: 'avoid',
      }}
    >
      <Box sx={{ display: 'flex' }}>{token}</Box>
      <Box>
        <Box
          component="span"
          sx={{ fontSize: docType.small, fontWeight: 700, color: INK, mr: 0.75 }}
        >
          {term}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: docType.small,
            color: colors.textSecondary,
            lineHeight: docLeading.normal,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function KeyBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3.5, breakInside: 'avoid' }}>
      <Typography
        sx={{
          fontSize: docType.label,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** A pill in the product's own colour for that value. */
function Swatch({ label, tone }: { label: string; tone: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: `${tone}1a`,
        border: `1px solid ${tone}59`,
        color: tone,
        fontSize: docType.label,
        fontWeight: 700,
        lineHeight: docLeading.snug,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

/** Every colour-coded value in the product, in one place. */
function ColourKeys() {
  return (
    <Box sx={{ mb: 4 }}>
      <KeyBlock title="Follow-up buckets">
        {BUCKET_ORDER.map((bucket) => {
          const meta = BUCKET_META[bucket];
          const tone = colors.bucketColors[bucket as keyof typeof colors.bucketColors] ?? GOLD;
          if (!meta) return null;
          return (
            <KeyRow key={bucket} token={<Swatch label={bucket} tone={tone} />} term={meta.action}>
              {meta.desc}
            </KeyRow>
          );
        })}
      </KeyBlock>

      <KeyBlock title="Visit outcomes">
        {OUTCOME_ORDER.map((outcome) => {
          const guide = OUTCOME_GUIDE[outcome];
          const tone = VISIT_OUTCOME_TONES[outcome];
          return (
            <KeyRow
              key={outcome}
              token={<Swatch label={guide.label} tone={tone} />}
              term={`${guide.cooldownDays}-day cooldown`}
            >
              {' — '}
              {guide.when}
            </KeyRow>
          );
        })}
      </KeyBlock>

      <KeyBlock title="Channels">
        {(['ON', 'OFF', 'ONLINE', 'NA'] as const).map((key) => {
          const style = PREMISES_STYLE[key];
          if (!style) return null;
          const term =
            key === 'ON'
              ? 'On-premise'
              : key === 'OFF'
                ? 'Off-premise'
                : key === 'ONLINE'
                  ? 'Online retailer'
                  : 'Other';
          return (
            <KeyRow key={key} token={<Swatch label={style.label} tone={style.color} />} term={term}>
              {' — '}
              {style.description.replace(/^[^—]*—\s*/, '')}
            </KeyRow>
          );
        })}
      </KeyBlock>

      <KeyBlock title="Momentum">
        <KeyRow token={<Swatch label="Growing" tone={colors.success} />} term="+10% or better">
          {' — recent three months ahead of the three before.'}
        </KeyRow>
        <KeyRow token={<Swatch label="Declining" tone={colors.error} />} term="−10% or worse">
          {' — slipping against the prior three months.'}
        </KeyRow>
        <KeyRow token={<Swatch label="Steady" tone={colors.textMuted} />} term="Within ±10%">
          {' — holding its level.'}
        </KeyRow>
        <KeyRow token={<Swatch label="New activity" tone={colors.info} />} term="No prior volume">
          {' — a new account, or a dormant one restarting.'}
        </KeyRow>
      </KeyBlock>

      <KeyBlock title={`Control states (${CONTROL_STATES.size})`}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            pt: 1,
            borderTop: `1px solid ${colors.borderLight}`,
          }}
        >
          {[...CONTROL_STATES].sort().map((code) => (
            <Swatch key={code} label={code} tone="#7c5cc4" />
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: docType.small,
            color: colors.textSecondary,
            mt: 1,
            lineHeight: docLeading.normal,
          }}
        >
          The state runs spirits distribution itself, so the liquor board is effectively the buyer.
          Every other state is a license state.
        </Typography>
      </KeyBlock>
    </Box>
  );
}

// =====================================================================
// Shared blocks
// =====================================================================

/** Renders `**bold**` and `` `code` `` in body copy. */
function Inline({ text }: { text: string }): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <Box key={i} component="strong" sx={{ fontWeight: 700, color: INK }}>
              {part.slice(2, -2)}
            </Box>
          );
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <Box
              key={i}
              component="code"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.85em',
                px: 0.5,
                borderRadius: 0.75,
                bgcolor: colors.surfaceSubtle,
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              {part.slice(1, -1)}
            </Box>
          );
        }
        return part;
      })}
    </>
  );
}

/**
 * Names one half of a two-part chapter.
 *
 * Sits between a chapter heading and its topics, so "Authentication" and
 * "Authorization" read as the two halves of one subject rather than an
 * unexplained change of topic halfway down. The technical word leads and
 * the plain one follows it, because both audiences are here: the term is
 * what an administrator will search for, the gloss is what everyone else
 * needs.
 */
function PartHeading({
  eyebrow,
  title,
  blurb,
  tone,
}: {
  eyebrow: string;
  title: string;
  blurb: string;
  /** Accent for this half, so the two are distinguishable at a glance. */
  tone: string;
}) {
  return (
    <Box sx={{ mt: 4, mb: 2.5, breakInside: 'avoid' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
        <Box aria-hidden sx={{ width: 22, height: '2px', bgcolor: tone, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: docType.label,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: tone,
          }}
        >
          {eyebrow}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: FONT_SERIF,
          fontSize: docType.h3,
          fontWeight: 600,
          color: INK,
          lineHeight: docLeading.tight,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: docType.small,
          color: colors.textSecondary,
          lineHeight: docLeading.normal,
          mt: 0.5,
        }}
      >
        {blurb}
      </Typography>
    </Box>
  );
}

/**
 * Accents for the Authentication & Authorization chapter.
 *
 * Colour carries meaning here rather than decoration: identity is one
 * subject, permissions another, and the two ways a session ends are told
 * apart by what causes them. Nothing is encoded ONLY in colour — every
 * accent sits beside a word that says the same thing, so the chapter
 * still reads correctly in print and to anyone who cannot separate the
 * hues.
 */
const AUTH_TONE = '#3f7d8c';
const AUTHZ_TONE = colors.goldDark;
const SESSION_TONE: Record<'inactivity' | 'time', string> = {
  // Something the platform does TO you, unprompted.
  inactivity: colors.warning,
  // Routine and largely invisible.
  time: '#3f7d8c',
};

/** The two ways a session ends, as a pair of accented cards. */
function SessionEndings() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.5,
        mb: 2.5,
      }}
    >
      {SESSION_ENDINGS.map((e) => {
        const tone = SESSION_TONE[e.tone];
        return (
          <Box
            key={e.trigger}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderLeft: `3px solid ${tone}`,
              breakInside: 'avoid',
            }}
          >
            <Typography
              sx={{
                fontSize: docType.label,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: tone,
              }}
            >
              {e.trigger}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_SERIF,
                fontSize: docType.h3,
                fontWeight: 600,
                color: INK,
                lineHeight: docLeading.tight,
                mt: 0.5,
                mb: 0.75,
              }}
            >
              {e.timing}
            </Typography>
            <Typography
              sx={{
                fontSize: docType.small,
                color: colors.textSecondary,
                lineHeight: docLeading.normal,
              }}
            >
              <Inline text={e.body} />
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * A rule the reader must not get wrong.
 *
 * Deliberately the only red thing in the chapter — a page where several
 * things shout has nothing left to say when one of them matters.
 */
function Caution({ title, body }: { title: string; body: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 2,
        mb: 3,
        borderRadius: 2,
        bgcolor: `${colors.error}0a`,
        border: `1px solid ${colors.error}33`,
        breakInside: 'avoid',
      }}
    >
      <ReportProblemOutlinedIcon
        sx={{ fontSize: 18, color: colors.error, flexShrink: 0, mt: '2px' }}
      />
      <Box>
        <Typography sx={{ fontSize: docType.small, fontWeight: 700, color: INK, mb: 0.4 }}>
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: docType.small,
            color: colors.textSecondary,
            lineHeight: docLeading.normal,
          }}
        >
          <Inline text={body} />
        </Typography>
      </Box>
    </Box>
  );
}

function Paragraphs({ items }: { items: GuideParagraph[] }) {
  return (
    <>
      {items.map((p, i) => (
        <Box key={i} sx={{ mb: 2 }}>
          {p.heading && (
            <Typography
              sx={{
                fontSize: docType.body,
                fontWeight: 700,
                color: INK,
                mb: 0.5,
                letterSpacing: '-0.01em',
              }}
            >
              {p.heading}
            </Typography>
          )}
          {p.body && (
            <Typography
              sx={{
                fontSize: docType.body,
                lineHeight: docLeading.relaxed,
                color: colors.textSecondary,
              }}
            >
              <Inline text={p.body} />
            </Typography>
          )}
          {p.bullets && (
            <Box component="ul" sx={{ pl: 2.5, mt: 0.75, mb: 0 }}>
              {p.bullets.map((b, j) => (
                <Box
                  key={j}
                  component="li"
                  sx={{
                    fontSize: docType.body,
                    lineHeight: docLeading.relaxed,
                    color: colors.textSecondary,
                    mb: 0.25,
                  }}
                >
                  <Inline text={b} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </>
  );
}

/**
 * The repeating unit of the manual: one feature, explained as
 * "what it is → how to use it → what to watch out for", with its
 * screenshot underneath.
 *
 * `description` is deliberately NOT rendered inside a chapter. It is the
 * dashboard's own one-liner, and next to `guide.what` it says the same
 * thing twice — which is the fastest way to make a manual feel padded.
 * Search results pass the chapter name through it instead, where the
 * reader genuinely needs to know which part of the guide a hit came from.
 */
function TopicBlock({
  id,
  title,
  description,
  guide,
  accent,
  onOpen,
  print,
}: {
  id: string;
  title: string;
  description?: string;
  guide: SectionGuide;
  accent: string;
  onOpen?: (src: string, caption: string) => void;
  print: boolean;
}) {
  return (
    <Box
      id={`topic-${id}`}
      sx={{
        mb: 4,
        pl: 2,
        borderLeft: `2px solid ${accent}33`,
        breakInside: print ? 'avoid' : undefined,
      }}
    >
      <Typography
        sx={{ fontSize: docType.h3, fontWeight: 700, color: INK, letterSpacing: '-0.01em' }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: docType.small, color: colors.textMuted, mt: 0.25 }}>
          {description}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: docType.body,
          lineHeight: docLeading.relaxed,
          color: colors.textSecondary,
          mt: 1.25,
        }}
      >
        <Inline text={guide.what} />
      </Typography>

      <Typography
        sx={{
          mt: 1.75,
          fontSize: docType.label,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: accent,
        }}
      >
        How to use it
      </Typography>
      <Box component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
        {guide.how.map((step, i) => (
          <Box
            key={i}
            component="li"
            sx={{
              fontSize: docType.body,
              lineHeight: docLeading.relaxed,
              color: colors.textSecondary,
              mb: 0.4,
            }}
          >
            <Inline text={step} />
          </Box>
        ))}
      </Box>

      {guide.tips && guide.tips.length > 0 && (
        <Box
          sx={{
            mt: 1.75,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: `${accent}08`,
            border: `1px solid ${accent}22`,
          }}
        >
          <Typography
            sx={{
              fontSize: docType.label,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: accent,
              mb: 0.5,
            }}
          >
            Good to know
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {guide.tips.map((tip, i) => (
              <Box
                key={i}
                component="li"
                sx={{
                  fontSize: docType.small,
                  lineHeight: docLeading.normal,
                  color: colors.textSecondary,
                  mb: 0.25,
                }}
              >
                <Inline text={tip} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {guide.admin && <AdminNote>{guide.admin}</AdminNote>}

      <Shot id={id} caption={title} onOpen={onOpen} print={print} />
    </Box>
  );
}

/** One-line framing under a chapter heading, from `CHAPTER_INTROS`. */
function ChapterIntro({ id }: { id: string }) {
  const text = CHAPTER_INTROS[id];
  if (!text) return null;
  return (
    <Typography
      sx={{
        fontSize: docType.body,
        lineHeight: docLeading.relaxed,
        color: colors.textSecondary,
        mb: 2,
      }}
    >
      {text}
    </Typography>
  );
}

function ChapterHeading({ chapter, print }: { chapter: Chapter; print: boolean }) {
  const tone = chapter.tone;
  const minutes = chapterReadingMinutes(chapter.id);
  return (
    <Box
      id={chapter.id}
      sx={{
        pt: print ? 0 : 2,
        mb: 3,
        scrollMarginTop: 88,
        breakBefore: print ? 'page' : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontSize: docType.label,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: tone,
          }}
        >
          Chapter {chapter.number}
        </Typography>
        {chapter.adminOnly && <AdminChip />}
        {minutes !== null && (
          <Typography
            sx={{
              fontSize: docType.label,
              fontWeight: 600,
              color: colors.textMuted,
              letterSpacing: '0.04em',
            }}
          >
            · {minutes} min read
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: FONT_SERIF,
          fontSize: { xs: 24, md: docType.h2 },
          fontWeight: 600,
          color: INK,
          lineHeight: docLeading.tight,
          mt: 0.5,
        }}
      >
        {chapter.title}
      </Typography>
      <Typography sx={{ fontSize: docType.body, color: colors.textMuted, mt: 0.75, maxWidth: 640 }}>
        {chapter.blurb}
      </Typography>
      <Box sx={{ mt: 2, height: 2, width: 56, bgcolor: tone, borderRadius: 1 }} />
    </Box>
  );
}

/**
 * Sticky contents rail.
 *
 * Hierarchy is carried by three cues rather than indentation alone:
 *
 *   1. Chapters own a numbered badge; topics never do. That single
 *      difference is what makes the two levels readable at a glance.
 *   2. Only the chapter you are currently reading shows its topics.
 *      Thirty-two entries at once is a wall; nine chapters plus the
 *      handful you are actually in is a map.
 *   3. Each open chapter's topics hang off their own short hairline in
 *      that chapter's accent colour, so the group reads as a unit
 *      instead of dissolving into the list.
 *
 * A single marker travels between entries rather than a border blinking
 * from row to row — one moving object reads as "you are here".
 */
function ContentsRail({
  chapters,
  activeId,
  onNavigate,
  fullWidth = false,
}: {
  chapters: TocChapter[];
  activeId: string | null;
  onNavigate: (id: string) => void;
  /** Mobile disclosure: fill the width and drop the inner scroll box. */
  fullWidth?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const [marker, setMarker] = useState<{ top: number; height: number } | null>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const openChapter = chapterOf(chapters, activeId);
  const openChapterId = openChapter?.id ?? null;
  const activeTone = openChapter?.tone ?? GOLD;

  /** Show a fade only on the side that actually has more content. */
  const syncEdges = () => {
    const box = scrollRef.current;
    if (!box) return;
    setEdges({
      top: box.scrollTop > 4,
      bottom: box.scrollTop + box.clientHeight < box.scrollHeight - 4,
    });
  };

  // Keep the marker glued to the active row, and the active row in view.
  //
  // A ResizeObserver on the inner content drives this rather than a plain
  // effect: expanding a chapter animates its height over ~250ms, so the
  // row's final position isn't known when `activeId` changes. Observing
  // the content re-measures on every frame of that animation instead.
  useEffect(() => {
    const place = () => {
      if (!activeId) {
        setMarker(null);
        return;
      }
      const el = itemRefs.current[activeId];
      const box = scrollRef.current;
      const inner = innerRef.current;
      if (!el || !box || !inner) return;

      // Measured against the scrolling content rather than `offsetTop`:
      // topic rows sit inside their chapter's wrapper, so `offsetTop`
      // would resolve against that wrapper and land the marker beside
      // the wrong row. Both rects are viewport-relative, and `inner`
      // moves with the scroll, so the difference is the row's true
      // position within the content.
      const top = Math.round(el.getBoundingClientRect().top - inner.getBoundingClientRect().top);
      const height = el.offsetHeight;
      setMarker({ top, height });

      const bottom = top + height;
      if (top < box.scrollTop) box.scrollTop = top - 20;
      else if (bottom > box.scrollTop + box.clientHeight) {
        box.scrollTop = bottom - box.clientHeight + 20;
      }
      syncEdges();
    };

    place();
    const inner = innerRef.current;
    if (!inner || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(place);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [activeId, openChapterId, chapters]);

  return (
    <Box
      component="nav"
      aria-label="Guide contents"
      sx={{ width: fullWidth ? '100%' : 248, flexShrink: 0 }}
    >
      {/* Eyebrow + gold hairline. The mobile panel's toggle already
          says "Contents", so it is dropped there. */}
      <Box
        sx={{
          display: fullWidth ? 'none' : 'flex',
          alignItems: 'center',
          gap: 1.25,
          mb: 1.75,
        }}
      >
        <Typography
          sx={{
            fontSize: docType.label,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            whiteSpace: 'nowrap',
          }}
        >
          Contents
        </Typography>
        <Box
          aria-hidden
          sx={{
            flex: 1,
            height: '1px',
            background: `linear-gradient(to right, ${GOLD}66, transparent)`,
          }}
        />
      </Box>

      <Box sx={{ position: 'relative' }}>
        {/* Edge fades — a scrollbar is noise at this size; a fade says
            "there is more" without drawing a control. */}
        {(['top', 'bottom'] as const).map((side) => (
          <Box
            key={side}
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              [side]: 0,
              height: 28,
              pointerEvents: 'none',
              zIndex: 2,
              opacity: edges[side] ? 1 : 0,
              transition: 'opacity 0.2s',
              background: `linear-gradient(to ${side === 'top' ? 'bottom' : 'top'}, ${
                colors.brandCream
              }, ${colors.brandCream}00)`,
            }}
          />
        ))}

        <Box
          ref={scrollRef}
          onScroll={syncEdges}
          sx={{
            maxHeight: fullWidth ? 'none' : 'calc(100vh - 160px)',
            overflowY: fullWidth ? 'visible' : 'auto',
            overflowX: 'hidden',
            // Scrollbar hidden deliberately — the edge fades carry the
            // affordance and a 4px sliver looks unfinished next to them.
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box ref={innerRef} sx={{ position: 'relative' }}>
            {/* The travelling marker */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                width: '3px',
                borderRadius: '0 2px 2px 0',
                bgcolor: activeTone,
                opacity: marker ? 1 : 0,
                top: marker ? `${marker.top}px` : 0,
                height: marker ? `${marker.height}px` : 0,
                transition:
                  'top 0.28s cubic-bezier(0.4, 0, 0.2, 1), height 0.28s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.28s',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />

            {chapters.map((chapter) => {
              const isOpen = chapter.id === openChapterId;
              const chapterActive = chapter.id === activeId;
              return (
                <Box key={chapter.id}>
                  {/* ---- Chapter row ---- */}
                  <Box
                    ref={(el: HTMLElement | null) => {
                      itemRefs.current[chapter.id] = el;
                    }}
                    component="a"
                    href={`#${chapter.id}`}
                    aria-current={chapterActive ? 'location' : undefined}
                    onClick={(e: MouseEvent) => {
                      e.preventDefault();
                      onNavigate(chapter.id);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.1,
                      textDecoration: 'none',
                      pl: 1.5,
                      pr: 1,
                      py: 0.85,
                      borderRadius: '0 8px 8px 0',
                      bgcolor: chapterActive ? `${chapter.tone}0f` : 'transparent',
                      transition: 'background-color 0.18s',
                      '&:hover': { bgcolor: `${chapter.tone}0f` },
                      '&:hover .toc-badge': {
                        borderColor: `${chapter.tone}66`,
                        color: chapter.tone,
                      },
                    }}
                  >
                    {/* Numbered badge — the one cue only chapters carry */}
                    <Box
                      className="toc-badge"
                      sx={{
                        flexShrink: 0,
                        width: 22,
                        height: 22,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: docType.label,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        border: '1px solid',
                        transition: 'background-color 0.18s, color 0.18s, border-color 0.18s',
                        ...(isOpen
                          ? {
                              bgcolor: chapter.tone,
                              borderColor: chapter.tone,
                              color: '#fff',
                            }
                          : {
                              bgcolor: 'transparent',
                              borderColor: colors.border,
                              color: colors.textMuted,
                            }),
                      }}
                    >
                      {chapter.number}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: docType.small,
                        fontWeight: isOpen ? 700 : 600,
                        lineHeight: docLeading.snug,
                        letterSpacing: '-0.005em',
                        color: isOpen ? chapter.tone : colors.textSecondary,
                        transition: 'color 0.18s',
                      }}
                    >
                      {chapter.title}
                    </Typography>
                    {chapter.adminOnly && (
                      <AdminPanelSettingsOutlinedIcon
                        titleAccess="Administrators only"
                        sx={{ fontSize: 14, color: GOLD, flexShrink: 0, ml: 'auto' }}
                      />
                    )}
                  </Box>

                  {/* ---- Topics ---- */}
                  {chapter.topics.length > 0 && (
                    <Collapse in={isOpen} timeout={260} unmountOnExit={false}>
                      <Box
                        sx={{
                          ml: 3.1,
                          pl: 1.5,
                          py: 0.4,
                          // The group's own hairline, in the chapter's colour.
                          borderLeft: `1px solid ${chapter.tone}33`,
                        }}
                      >
                        {chapter.topics.map((t) => {
                          const active = t.id === activeId;
                          return (
                            <Box
                              key={t.id}
                              ref={(el: HTMLElement | null) => {
                                itemRefs.current[t.id] = el;
                              }}
                              component="a"
                              href={`#${t.id}`}
                              aria-current={active ? 'location' : undefined}
                              onClick={(e: MouseEvent) => {
                                e.preventDefault();
                                onNavigate(t.id);
                              }}
                              sx={{
                                display: 'block',
                                textDecoration: 'none',
                                py: 0.42,
                                pr: 1,
                                fontSize: docType.small,
                                lineHeight: docLeading.snug,
                                fontWeight: active ? 600 : 400,
                                color: active ? chapter.tone : colors.textMuted,
                                transition: 'color 0.15s',
                                '&:hover': { color: chapter.tone },
                              }}
                            >
                              {t.label}
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Search field for the guide.
 *
 * Styled to sit *in* the page rather than on top of it: warm ivory
 * field on the cream ground, gold hairline instead of MUI's grey
 * outline, and a gold focus ring — the same treatment the rest of the
 * platform's inputs get. A default MUI `TextField` reads as a foreign
 * white box against this background.
 *
 * Carries the two affordances people expect from documentation search:
 * a keyboard shortcut to focus it, and Escape to get out.
 */
function GuideSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);

  // ⌘K on Apple hardware, Ctrl+K elsewhere. Resolved once — the label
  // has to match the key that actually works.
  const isApple = useMemo(
    () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent),
    [],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      // ⌘K / Ctrl+K from anywhere.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      // "/" is the other convention, but only when not already typing —
      // otherwise it swallows the character mid-word.
      //
      // Focus is deferred on purpose. Moving focus synchronously during
      // keydown hands the still-in-flight keystroke to the newly focused
      // input, and a literal "/" lands in the box; preventDefault on the
      // original target does not stop that. A timer rather than
      // requestAnimationFrame, which does not run in a throttled tab.
      if (e.key === '/' && !typing) {
        e.preventDefault();
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const active = focused || value.length > 0;

  return (
    <Box
      sx={{
        mb: 4,
        // Match the content column so the field's edges line up with
        // the title and body beneath it.
        maxWidth: 740,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        height: 46,
        borderRadius: 2.5,
        bgcolor: focused ? '#fff' : 'rgba(255, 255, 255, 0.6)',
        border: '1px solid',
        borderColor: focused ? GOLD : `${GOLD}33`,
        boxShadow: focused ? `0 0 0 3px ${GOLD}1f` : 'none',
        transition: 'border-color 0.18s, box-shadow 0.18s, background-color 0.18s',
        cursor: 'text',
        '&:hover': { borderColor: focused ? GOLD : `${GOLD}66` },
      }}
      onClick={() => inputRef.current?.focus()}
    >
      <SearchIcon
        sx={{
          fontSize: 19,
          flexShrink: 0,
          color: active ? GOLD : colors.textMuted,
          transition: 'color 0.18s',
        }}
      />
      <Box
        component="input"
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Escape') {
            if (value) onChange('');
            else inputRef.current?.blur();
          }
        }}
        placeholder="Search the guide"
        aria-label="Search the guide"
        sx={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: 'inherit',
          fontSize: docType.body,
          color: colors.textPrimary,
          '&::placeholder': { color: colors.textMuted, opacity: 1 },
        }}
      />

      {value ? (
        <Box
          component="button"
          type="button"
          aria-label="Clear search"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onChange('');
            inputRef.current?.focus();
          }}
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            p: 0,
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            bgcolor: 'transparent',
            color: colors.textMuted,
            '&:hover': { bgcolor: `${GOLD}1a`, color: GOLD },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
      ) : (
        // Shortcut hint. Hidden on touch, where there is no keyboard to
        // press and the chip is just clutter.
        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 0.25,
            px: 0.75,
            height: 21,
            borderRadius: 1,
            border: `1px solid ${GOLD}33`,
            bgcolor: `${GOLD}0d`,
            color: colors.textMuted,
            fontSize: docType.label,
            fontWeight: 600,
            letterSpacing: '0.02em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {isApple ? '⌘K' : 'Ctrl K'}
        </Box>
      )}
    </Box>
  );
}

/** Panel open/close duration, shared by the animation and the
 *  navigation delay that waits for it. */
const COLLAPSE_MS = 220;

/**
 * Contents for viewports too narrow for the sidebar rail.
 *
 * Below `md` the rail is hidden, which would otherwise leave a
 * nine-chapter document with no navigation at all on a phone or an iPad
 * in portrait — and Regional Sales is explicitly a phone surface.
 *
 * A disclosure rather than a drawer: it names the chapter you are in
 * even while closed, which is the thing you most often want to know,
 * and it costs one row of vertical space.
 */
function MobileContents({
  chapters,
  activeId,
  onNavigate,
}: {
  chapters: TocChapter[];
  activeId: string | null;
  onNavigate: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = chapterOf(chapters, activeId);

  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'none' },
        mb: 3,
        border: `1px solid ${GOLD}33`,
        borderRadius: 2.5,
        bgcolor: 'rgba(255, 255, 255, 0.6)',
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 1.25,
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
        }}
      >
        <MenuBookOutlinedIcon sx={{ fontSize: 18, color: GOLD, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: docType.label,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            Contents
          </Box>
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: docType.small,
              fontWeight: 600,
              color: current?.tone ?? INK,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {current ? `${current.number}. ${current.title}` : 'Jump to a chapter'}
          </Box>
        </Box>
        <KeyboardArrowDownRoundedIcon
          sx={{
            fontSize: 20,
            color: colors.textMuted,
            flexShrink: 0,
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      <Collapse in={open} timeout={COLLAPSE_MS}>
        <Box sx={{ px: 1.25, pb: 1.5, borderTop: `1px solid ${GOLD}22`, pt: 1 }}>
          <ContentsRail
            chapters={chapters}
            activeId={activeId}
            fullWidth
            onNavigate={(id) => {
              setOpen(false);
              // Wait for the panel to finish collapsing before scrolling.
              // It sits above the content, so closing it shortens the
              // page — scrolling first lands a few hundred pixels past
              // the target.
              window.setTimeout(() => onNavigate(id), COLLAPSE_MS + 20);
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

// =====================================================================
// Page
// =====================================================================

export function UserGuidePage() {
  const [params] = useSearchParams();
  const print = params.get('print') === '1';

  const [query, setQuery] = useState('');
  const [lightbox, setLightbox] = useState<{ src: string; caption: string } | null>(null);

  const q = query.trim().toLowerCase();
  const searching = q.length > 1;

  // Flatten every documented topic once so search can scan all three
  // areas without each chapter re-deriving its own list.
  const allTopics = useMemo(() => {
    const depletions = depletionsSectionsByCategory().flatMap((g) =>
      g.sections.map((s) => ({
        id: s.id,
        title: s.title,
        description: s.description,
        guide: s.guide,
        chapter: 'Depletions',
        accent: CATEGORY_ACCENT[s.category],
      })),
    );
    const rep = [...REGIONAL_SALES_TABS, ...REGIONAL_SALES_ACTIONS].map((t: GuideTopic) => ({
      ...t,
      chapter: 'Regional Sales',
      accent: CHAPTER.regionalSales.tone,
    }));
    const admin = OVERSIGHT_TOPICS.map((t: GuideTopic) => ({
      ...t,
      chapter: 'Regional Sales Oversight',
      accent: CHAPTER.oversight.tone,
    }));
    return [...depletions, ...rep, ...admin];
  }, []);

  const matches = useMemo(() => {
    if (!searching) return [];
    const hay = (t: (typeof allTopics)[number]) =>
      [t.title, t.description ?? '', t.guide.what, ...t.guide.how, ...(t.guide.tips ?? [])]
        .join(' ')
        .toLowerCase();
    return allTopics.filter((t) => hay(t).includes(q));
  }, [allTopics, q, searching]);

  const glossaryMatches = useMemo(() => {
    if (!searching) return [];
    return GLOSSARY.filter(
      (g) => g.term.toLowerCase().includes(q) || g.definition.toLowerCase().includes(q),
    );
  }, [q, searching]);

  // The print route is rendered headless by the capture script. Setting
  // the document title makes the generated PDF's metadata read properly
  // in a file manager instead of showing the app's default title.
  useEffect(() => {
    if (!print) return;
    const prev = document.title;
    document.title = `Hooten Young — User Guide v${GUIDE_VERSION}`;
    return () => {
      document.title = prev;
    };
  }, [print]);

  // Contents + scroll spy. The spy is off in print mode (no sidebar) and
  // while searching (the chapters aren't rendered, so the anchors it
  // measures don't exist).
  const toc = useMemo(() => buildToc(), []);
  const tocIds = useMemo(() => flattenToc(toc), [toc]);
  const { activeId, jumpTo } = useScrollSpy(tocIds, !print && !searching);

  /** Sidebar click: scroll there, and put the anchor in the URL without
   *  letting the browser's own jump fight the smooth scroll. */
  const goToEntry = (id: string) => {
    if (searching) setQuery('');
    // Mark the destination current straight away, so the rail opens the
    // chapter you asked for instead of every chapter on the way there.
    jumpTo(id);
    // One frame, so a cleared search has re-rendered the chapters before
    // we try to measure where the target sits.
    requestAnimationFrame(() => {
      scrollToEntry(id);
      window.history.replaceState(null, '', `#${id}`);
    });
  };

  // Deep links like /user-guide#glossary arrive before React has
  // rendered the chapters, so the browser's own hash scroll finds
  // nothing and gives up. Re-run it once the content is on the page.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ block: 'start' });
  }, [hash]);

  const openShot = (src: string, caption: string) => setLightbox({ src, caption });

  const body = (
    <Box
      sx={{
        // Media width. Screenshots and cards use the full column.
        maxWidth: 740,
        // Measure. 740px of 16px Inter runs ~88 characters per line;
        // the typographic convention for body text is 65–75, and long
        // lines are the main reason a wide page is tiring to read.
        // 640px lands at ~76. Constraining the text elements rather
        // than the column keeps images at full width.
        '& p, & li': { maxWidth: 640 },
      }}
    >
      {/* ---------------- Title block ---------------- */}
      <Box sx={{ mb: print ? 6 : 5 }}>
        <Typography
          sx={{
            fontSize: docType.label,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: GOLD,
          }}
        >
          Hooten Young · Internal
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_SERIF,
            fontSize: { xs: 32, md: docType.h1 },
            fontWeight: 600,
            color: INK,
            lineHeight: docLeading.tight,
            mt: 1,
          }}
        >
          User Guide
        </Typography>
        <Typography
          sx={{ fontSize: docType.lead, color: colors.textSecondary, mt: 1.5, maxWidth: 620 }}
        >
          How to use Depletions, Regional Sales, and Regional Sales Oversight.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            size="small"
            label={`Version ${GUIDE_VERSION}`}
            sx={{ bgcolor: `${GOLD}18`, color: GOLD, fontWeight: 700, fontSize: docType.label }}
          />
          <Chip
            size="small"
            label={`Updated ${formatDate(GUIDE_REVISIONS[0]?.date ?? '')}`}
            sx={{ bgcolor: colors.surfaceSubtle, color: colors.textMuted, fontSize: docType.label }}
          />
          <Chip
            size="small"
            label={`${totalReadingMinutes()} min read`}
            sx={{ bgcolor: colors.surfaceSubtle, color: colors.textMuted, fontSize: docType.label }}
          />
          <Chip
            size="small"
            label="Confidential — do not distribute"
            sx={{
              bgcolor: '#dc262612',
              color: '#dc2626',
              fontWeight: 600,
              fontSize: docType.label,
            }}
          />
        </Box>
      </Box>

      {/* ---------------- Printed contents ----------------
          The PDF has no sidebar, so it gets a real contents page.
          Deliberately without page numbers: they can't be known until
          the PDF is paginated, and a wrong number is worse than none.
          It still earns its place as an at-a-glance map of the guide. */}
      {print && (
        <Box sx={{ mb: 5, breakAfter: 'page' }}>
          <Typography
            sx={{
              fontFamily: FONT_SERIF,
              fontSize: docType.h2,
              fontWeight: 600,
              color: INK,
              mb: 2.5,
            }}
          >
            Contents
          </Typography>
          {toc.map((chapter) => (
            <Box key={chapter.id} sx={{ mb: 2, breakInside: 'avoid' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: 0.75,
                    bgcolor: chapter.tone,
                    color: '#fff',
                    fontSize: docType.label,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {chapter.number}
                </Box>
                <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                  {chapter.title}
                </Typography>
              </Box>
              {chapter.topics.length > 0 && (
                <Box sx={{ ml: 2.4, mt: 0.5, pl: 1.5, borderLeft: `1px solid ${chapter.tone}33` }}>
                  {chapter.topics.map((t) => (
                    <Typography
                      key={t.id}
                      sx={{
                        fontSize: docType.small,
                        color: colors.textMuted,
                        py: 0.14,
                        lineHeight: docLeading.normal,
                      }}
                    >
                      {t.label}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* ---------------- Search results ---------------- */}
      {searching && (
        <Box sx={{ mb: 5 }}>
          <Typography sx={{ fontSize: docType.small, color: colors.textMuted, mb: 2 }}>
            {matches.length + glossaryMatches.length} result
            {matches.length + glossaryMatches.length === 1 ? '' : 's'} for “{query}”
          </Typography>
          {glossaryMatches.map((g) => (
            <Box key={g.term} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                {g.term}{' '}
                <Box
                  component="span"
                  sx={{ fontWeight: 500, color: colors.textMuted, fontSize: docType.small }}
                >
                  · Glossary
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: docType.small,
                  lineHeight: docLeading.normal,
                  color: colors.textSecondary,
                }}
              >
                {g.definition}
              </Typography>
            </Box>
          ))}
          {matches.map((t) => (
            <TopicBlock
              key={`${t.chapter}-${t.id}`}
              id={t.id}
              title={t.title}
              description={t.chapter}
              guide={t.guide}
              accent={t.accent}
              onOpen={openShot}
              print={print}
            />
          ))}
          {matches.length + glossaryMatches.length === 0 && (
            <Typography sx={{ fontSize: docType.body, color: colors.textMuted }}>
              Nothing matched. Try a shorter word — “bucket”, “pin”, “premise”.
            </Typography>
          )}
        </Box>
      )}

      {!searching && (
        <>
          {/* -------- Before you start -------- */}
          <ChapterHeading chapter={CHAPTER.introduction} print={print} />
          <Paragraphs items={INTRODUCTION} />
          <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK, mt: 3, mb: 1.5 }}>
            The three areas this guide covers
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
            {AREA_OVERVIEW.map((a) => (
              <Box
                key={a.name}
                sx={{
                  p: 2,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2,
                  bgcolor: colors.cardBg,
                  breakInside: 'avoid',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                    {a.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: docType.label,
                      color: colors.textMuted,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {a.path}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: docType.small,
                    color: colors.textSecondary,
                    mt: 0.5,
                    lineHeight: docLeading.normal,
                  }}
                >
                  {a.summary}
                </Typography>
                <Typography sx={{ fontSize: docType.small, color: colors.textMuted, mt: 0.75 }}>
                  <strong>For:</strong> {a.audience} · <strong>Needs the role:</strong> {a.role}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* -------- Getting started -------- */}
          <ChapterHeading chapter={CHAPTER.gettingStarted} print={print} />
          <Box sx={{ mb: 3 }}>
            {GETTING_STARTED.map((step, i) => (
              <Box key={step.title} sx={{ display: 'flex', gap: 2, mb: 2.5, breakInside: 'avoid' }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: `${GOLD}18`,
                    color: GOLD,
                    fontSize: docType.small,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {i + 1}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: docType.body,
                      lineHeight: docLeading.relaxed,
                      color: colors.textSecondary,
                      mt: 0.25,
                    }}
                  >
                    {step.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Shot
            id="home"
            caption="The home page — you see only the sections your roles allow."
            onOpen={openShot}
            print={print}
          />

          {/* -------- Depletions -------- */}
          <ChapterHeading chapter={CHAPTER.depletions} print={print} />
          <ChapterIntro id="depletions" />
          <Shot
            id="depletions-overview"
            caption="The Depletions page as it opens — headline figures, then the collapsed sections."
            onOpen={openShot}
            print={print}
          />
          <Box sx={{ mt: 4 }}>
            {depletionsSectionsByCategory().map((group) => (
              <Box key={group.category} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: group.accent }} />
                  <Typography
                    sx={{
                      fontSize: docType.small,
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: group.accent,
                    }}
                  >
                    {group.category}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: docType.small, color: colors.textMuted, mb: 2.5, pl: 2.5 }}
                >
                  {group.purpose}
                </Typography>
                {group.sections.map((s) => (
                  <TopicBlock
                    key={s.id}
                    id={s.id}
                    title={s.title}
                    guide={s.guide}
                    accent={group.accent}
                    onOpen={openShot}
                    print={print}
                  />
                ))}
              </Box>
            ))}
          </Box>

          {/* -------- Regional Sales -------- */}
          <ChapterHeading chapter={CHAPTER.regionalSales} print={print} />
          <ChapterIntro id="regional-sales" />
          <Shot
            id="regional-sales-overview"
            caption="The Regional Sales workspace, opening on Today’s Plan."
            onOpen={openShot}
            print={print}
          />
          <Typography
            sx={{
              fontSize: docType.small,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: CHAPTER.regionalSales.tone,
              mt: 4,
              mb: 2.5,
            }}
          >
            The five tabs
          </Typography>
          {REGIONAL_SALES_TABS.map((t) => (
            <TopicBlock
              key={t.id}
              id={t.id}
              title={t.title}
              guide={t.guide}
              accent={CHAPTER.regionalSales.tone}
              onOpen={openShot}
              print={print}
            />
          ))}
          <Typography
            sx={{
              fontSize: docType.small,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: CHAPTER.regionalSales.tone,
              mt: 2,
              mb: 2.5,
            }}
          >
            Working an account
          </Typography>
          {REGIONAL_SALES_ACTIONS.map((t) => (
            <TopicBlock
              key={t.id}
              id={t.id}
              title={t.title}
              guide={t.guide}
              accent={CHAPTER.regionalSales.tone}
              onOpen={openShot}
              print={print}
            />
          ))}

          {/* -------- Oversight -------- */}
          <ChapterHeading chapter={CHAPTER.oversight} print={print} />
          <ChapterIntro id="oversight" />
          <Shot
            id="oversight-overview"
            caption="Regional Sales Oversight — the rep roster beside the activity feed."
            onOpen={openShot}
            print={print}
          />
          <Box sx={{ mt: 4 }}>
            {OVERSIGHT_TOPICS.map((t) => (
              <TopicBlock
                key={t.id}
                id={t.id}
                title={t.title}
                guide={t.guide}
                accent={CHAPTER.oversight.tone}
                onOpen={openShot}
                print={print}
              />
            ))}
          </Box>

          {/* -------- Glossary -------- */}
          <ChapterHeading chapter={CHAPTER.glossary} print={print} />
          <ColourKeys />
          {GLOSSARY_AREAS.map((area) => {
            const entries = GLOSSARY.filter((g) => g.area === area);
            if (entries.length === 0) return null;
            return (
              <Box key={area} sx={{ mb: 3.5 }}>
                <Typography
                  sx={{
                    fontSize: docType.label,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: CHAPTER.glossary.tone,
                    mb: 1.5,
                  }}
                >
                  {area}
                </Typography>
                {entries.map((g) => (
                  <Box key={g.term} sx={{ mb: 1.75, breakInside: 'avoid' }}>
                    <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                      {g.term}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: docType.small,
                        lineHeight: docLeading.normal,
                        color: colors.textSecondary,
                      }}
                    >
                      {g.definition}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })}

          {/* -------- FAQ -------- */}
          <ChapterHeading chapter={CHAPTER.faq} print={print} />
          {FAQ.map((f) => (
            <Box key={f.question} sx={{ mb: 2.25, breakInside: 'avoid' }}>
              <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                {f.question}
              </Typography>
              <Typography
                sx={{
                  fontSize: docType.body,
                  lineHeight: docLeading.relaxed,
                  color: colors.textSecondary,
                  mt: 0.25,
                }}
              >
                {f.answer}
              </Typography>
            </Box>
          ))}

          {/* -------- Authentication & Authorization --------
               Two halves, named. They were once separate places in the
               guide, which meant one change to sessions needed edits in
               four of them and was easy to leave half-done. */}
          <ChapterHeading chapter={CHAPTER.access} print={print} />

          <PartHeading
            eyebrow="Authentication"
            title="User identity"
            blurb="How you prove you are you, and how long that lasts."
            tone={AUTH_TONE}
          />
          <Paragraphs items={AUTH_TOPICS} />

          <Typography
            sx={{
              fontSize: docType.small,
              fontWeight: 700,
              color: INK,
              mt: 2.5,
              mb: 1.25,
            }}
          >
            Sessions end on their own
          </Typography>
          <SessionEndings />
          <Typography
            sx={{
              fontSize: docType.small,
              color: colors.textSecondary,
              lineHeight: docLeading.relaxed,
              mb: 3,
            }}
          >
            <Inline text={SESSION_RATIONALE} />
          </Typography>

          <Caution title={AUTH_CAUTION.title} body={AUTH_CAUTION.body} />

          <PartHeading
            eyebrow="Authorization"
            title="User access and permissions"
            blurb="What your account is allowed to reach once you are in."
            tone={AUTHZ_TONE}
          />
          <Box sx={{ mb: 3 }}>
            {ROLES.map((r) => (
              <Box
                key={r.name}
                sx={{
                  p: 2,
                  mb: 1.5,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 2,
                  bgcolor: colors.cardBg,
                  breakInside: 'avoid',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
                    {r.displayName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: docType.label,
                      color: colors.textMuted,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {r.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: docType.small,
                    lineHeight: docLeading.normal,
                    color: colors.textSecondary,
                    mt: 0.5,
                  }}
                >
                  {r.grants}
                </Typography>
              </Box>
            ))}
          </Box>
          <Paragraphs items={ACCESS_NOTES} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 3.5, mb: 1.75 }}>
            <Typography sx={{ fontSize: docType.body, fontWeight: 700, color: INK }}>
              What administrators can do
            </Typography>
            <AdminChip label="Admin" />
          </Box>
          {ADMIN_CAPABILITIES.map((cap) => (
            <Box
              key={cap.path}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '190px 1fr' },
                columnGap: 2,
                rowGap: 0.25,
                py: 1.25,
                borderTop: `1px solid ${colors.borderLight}`,
                breakInside: 'avoid',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: docType.small, fontWeight: 700, color: INK }}>
                  {cap.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: docType.label,
                    color: colors.textMuted,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  {cap.path}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: docType.small,
                  lineHeight: docLeading.normal,
                  color: colors.textSecondary,
                }}
              >
                {cap.summary}
              </Typography>
            </Box>
          ))}

          {/* -------- Feedback -------- */}
          <ChapterHeading chapter={CHAPTER.feedback} print={print} />
          <Typography
            sx={{
              fontSize: docType.body,
              lineHeight: docLeading.relaxed,
              color: colors.textSecondary,
              mb: 2.5,
            }}
          >
            <Inline text={FEEDBACK_INTRO} />
          </Typography>

          {/* The four categories, in the colours the dialog itself uses. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
            {FEEDBACK_CATEGORIES.map((cat) => (
              <Box
                key={cat.value}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.6,
                  borderRadius: 1.5,
                  bgcolor: cat.activeBg,
                  border: `1px solid ${cat.activeBorder}`,
                  color: cat.activeText,
                  fontSize: docType.small,
                  fontWeight: 700,
                }}
              >
                <span aria-hidden>{cat.emoji}</span>
                {cat.label}
              </Box>
            ))}
          </Box>

          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {FEEDBACK_NOTES.map((note, i) => (
              <Box
                key={i}
                component="li"
                sx={{
                  fontSize: docType.body,
                  lineHeight: docLeading.relaxed,
                  color: colors.textSecondary,
                  mb: 0.5,
                }}
              >
                <Inline text={note} />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );

  // -------------------- print mode --------------------
  if (print) {
    return (
      <Box sx={{ bgcolor: '#fff', px: 5, py: 4 }} data-guide-print="ready">
        {body}
      </Box>
    );
  }

  // -------------------- normal mode --------------------
  return (
    <SiteLayout subtitle="User Guide">
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 5, md: 8 } }}>
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start' }}>
          {/* Contents rail — travelling marker tracks the section on screen */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'sticky',
              top: 88,
              flexShrink: 0,
            }}
          >
            <ContentsRail chapters={toc} activeId={activeId} onNavigate={goToEntry} />
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <GuideSearch value={query} onChange={setQuery} />
            <MobileContents chapters={toc} activeId={activeId} onNavigate={goToEntry} />
            {body}
          </Box>
        </Box>
      </Container>

      {/* Screenshot lightbox */}
      <Dialog
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#fff', p: 2 } }}
      >
        {lightbox && (
          <>
            <Box
              component="img"
              src={lightbox.src}
              alt={lightbox.caption}
              sx={{ width: '100%', display: 'block', borderRadius: 1 }}
            />
            <Typography sx={{ mt: 1, fontSize: docType.small, color: colors.textMuted }}>
              {lightbox.caption}
            </Typography>
          </>
        )}
      </Dialog>
    </SiteLayout>
  );
}

/** "2026-09-02" → "September 2, 2026", timezone-stable. */
function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${months[m - 1]} ${d}, ${y}`;
}
